import React from "react";
import PropTypes from "prop-types";
import ErrorBoundary from "../shared/error/ErrorBoundary";
import EditForm from "./EditForm";
import RecruiterJobOptions from "./RecruiterJobOptions";
import JobProfileLeft from "./JobProfileLeft";
import JobProfileRight from "./JobProfileRight";
import feedbackTimescaleOptionsType from "./types/feedbackTimescaleOptionsType";
import jobStatusWidgetType from "./types/jobStatusWidgetType";
import notificationRecipientsType from "./types/notificationRecipientsType";
import audienceMembersType from "./types/audienceMembersType";
import sectorOptionsType from "./types/sectorOptionsType";
import vacancyReasonOptionsType from "./types/vacancyReasonOptionsType";
import errorsType from "./types/errorsType";
import jobProfileType from "./types/jobProfileType";
import {
  JobProfileWrapper,
  JobProfileInner,
} from "./styles/job-profile-styles";

const JobProfileView = ({
  accountManager,
  accountManagerAvatar,
  applicationsAtOfferOrAccepted,
  agencyBriefing,
  audienceId,
  audienceMemberIds,
  audienceMembers,
  benefitIds,
  benefitOptions,
  burst,
  closeJobUrl,
  considerRelocation,
  contractDuration,
  contractDurationInterval,
  copyJobUrl,
  currencyId,
  currencyOptions,
  currencySymbol,
  cvLimitPerRecruiter,
  dateCreated,
  degreeRequired,
  description,
  editModalActive,
  employerAccountType,
  engagementId,
  errors,
  exclusive,
  feedbackTimescaleId,
  feedbackTimescaleOptions,
  finalSalary,
  handleModalShow,
  handleNext,
  handlePrev,
  interviewComments,
  interviewStages,
  isJobActive,
  isPortalEmployer,
  isRecruiter,
  jobStatus,
  jobStatusWidget,
  jobTitle,
  jobType,
  keySkills,
  loading,
  location,
  messageEmployerUrl,
  notificationRecipientIds,
  notificationRecipients,
  positionFilled,
  postcode,
  publishedStatus,
  recruiterFeePercentage,
  salaryMax,
  salaryMin,
  salaryRange,
  salaryType,
  sectorId,
  sectorOptions,
  step,
  updateJobProfile,
  vacancyReason,
  vacancyReasonOptions,
  validate,
  visaSponsorship,
  numberOfPositions,
  isPortalUser,
  jobProfile,
  recruiterCompanyName,
}) => (
  <ErrorBoundary>
    <JobProfileWrapper>
      {isRecruiter && (
        <RecruiterJobOptions
          jobProfile={jobProfile}
          isPortalUser={isPortalUser}
          engagementId={engagementId}
          messageEmployerUrl={messageEmployerUrl}
          recruiterCompanyName={recruiterCompanyName}
        />
      )}
      {!isRecruiter && (
        <EditForm
          agencyBriefing={agencyBriefing}
          applicationsAtOfferOrAccepted={applicationsAtOfferOrAccepted}
          audienceId={audienceId}
          audienceMemberIds={audienceMemberIds}
          audienceMembers={audienceMembers}
          benefitIds={benefitIds}
          burst={burst}
          closeJobUrl={closeJobUrl}
          considerRelocation={considerRelocation}
          contractDuration={contractDuration}
          contractDurationInterval={contractDurationInterval}
          copyJobUrl={copyJobUrl}
          cvLimitPerRecruiter={cvLimitPerRecruiter}
          degreeRequired={degreeRequired}
          description={description}
          editModalActive={editModalActive}
          errors={errors}
          feedbackTimescaleId={feedbackTimescaleId}
          feedbackTimescaleOptions={feedbackTimescaleOptions}
          handleModalShow={handleModalShow}
          handleNext={handleNext}
          handlePrev={handlePrev}
          interviewComments={interviewComments}
          interviewStages={interviewStages}
          isJobActive={isJobActive}
          isPortalEmployer={isPortalEmployer}
          jobTitle={jobTitle}
          jobStatus={jobStatus}
          jobType={jobType}
          keySkills={keySkills}
          loading={loading}
          location={location}
          notificationRecipientIds={notificationRecipientIds}
          notificationRecipients={notificationRecipients}
          postcode={postcode}
          recruiterFeePercentage={recruiterFeePercentage}
          salaryMax={salaryMax}
          salaryMin={salaryMin}
          salaryType={salaryType}
          sectorId={sectorId}
          sectorOptions={sectorOptions}
          step={step}
          updateJobProfile={updateJobProfile}
          vacancyReason={vacancyReason}
          vacancyReasonOptions={vacancyReasonOptions}
          validate={validate}
          visaSponsorship={visaSponsorship}
          numberOfPositions={numberOfPositions}
          currencySymbol={currencySymbol}
          currencyOptions={currencyOptions}
          currencyId={currencyId}
        />
      )}
      <JobProfileInner>
        <JobProfileLeft
          agencyBriefing={agencyBriefing}
          benefitIds={benefitIds}
          benefitOptions={benefitOptions}
          considerRelocation={considerRelocation}
          contractDuration={contractDuration}
          contractDurationInterval={contractDurationInterval}
          degreeRequired={degreeRequired}
          description={description}
          employerAccountType={employerAccountType}
          jobTitle={jobTitle}
          jobType={jobType}
          keySkills={keySkills}
          publishedStatus={publishedStatus}
          recruiterFeePercentage={recruiterFeePercentage}
          salaryMax={salaryMax}
          salaryMin={salaryMin}
          salaryRange={salaryRange}
          salaryType={salaryType}
          vacancyReason={vacancyReason}
          vacancyReasonOptions={vacancyReasonOptions}
          visaSponsorship={visaSponsorship}
          isRecruiter={isRecruiter}
          exclusive={exclusive}
          finalSalary={finalSalary}
          positionFilled={positionFilled}
          isPortalUser={isPortalUser}
          currencySymbol={currencySymbol}
        />
        <JobProfileRight
          jobStatus={jobStatus}
          dateCreated={dateCreated}
          location={location}
          postcode={postcode}
          feedbackTimescaleId={feedbackTimescaleId}
          feedbackTimescaleOptions={feedbackTimescaleOptions}
          accountManager={accountManager}
          accountManagerAvatar={accountManagerAvatar}
          interviewComments={interviewComments}
          interviewStages={interviewStages}
          jobStatusWidget={jobStatusWidget}
          isRecruiter={isRecruiter}
          numberOfPositions={numberOfPositions}
        />
      </JobProfileInner>
    </JobProfileWrapper>
  </ErrorBoundary>
);

JobProfileView.propTypes = {
  jobProfile: jobProfileType.isRequired,
  accountManager: PropTypes.string.isRequired,
  accountManagerAvatar: PropTypes.string.isRequired,
  agencyBriefing: PropTypes.string,
  applicationsAtOfferOrAccepted: PropTypes.bool.isRequired,
  audienceId: PropTypes.number,
  benefitIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  benefitOptions: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ).isRequired,
  closeJobUrl: PropTypes.string.isRequired,
  considerRelocation: PropTypes.bool.isRequired,
  contractDuration: PropTypes.number,
  contractDurationInterval: PropTypes.string,
  copyJobUrl: PropTypes.string.isRequired,
  employerAccountType: PropTypes.string.isRequired,
  engagementId: PropTypes.number,
  messageEmployerUrl: PropTypes.string.isRequired,
  cvLimitPerRecruiter: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  dateCreated: PropTypes.string,
  degreeRequired: PropTypes.bool.isRequired,
  description: PropTypes.string.isRequired,
  editModalActive: PropTypes.bool.isRequired,
  errors: errorsType.isRequired,
  feedbackTimescaleId: PropTypes.number.isRequired,
  feedbackTimescaleOptions: feedbackTimescaleOptionsType.isRequired,
  handleModalShow: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handlePrev: PropTypes.func.isRequired,
  interviewComments: PropTypes.string,
  interviewStages: PropTypes.number.isRequired,
  isJobActive: PropTypes.bool.isRequired,
  isRecruiter: PropTypes.bool,
  jobStatus: PropTypes.string,
  jobTitle: PropTypes.string.isRequired,
  jobType: PropTypes.string.isRequired,
  keySkills: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.string.isRequired,
  notificationRecipientIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  notificationRecipients: notificationRecipientsType.isRequired,
  postcode: PropTypes.string,
  publishedStatus: PropTypes.string.isRequired,
  recruiterFeePercentage: PropTypes.string.isRequired,
  salaryMax: PropTypes.number,
  salaryMin: PropTypes.number.isRequired,
  salaryRange: PropTypes.string,
  salaryType: PropTypes.string.isRequired,
  sectorId: PropTypes.number.isRequired,
  sectorOptions: sectorOptionsType.isRequired,
  step: PropTypes.number,
  updateJobProfile: PropTypes.func.isRequired,
  vacancyReason: PropTypes.number,
  vacancyReasonOptions: vacancyReasonOptionsType.isRequired,
  validate: PropTypes.func.isRequired,
  visaSponsorship: PropTypes.bool.isRequired,
  jobStatusWidget: jobStatusWidgetType,
  exclusive: PropTypes.bool.isRequired,
  finalSalary: PropTypes.number,
  positionFilled: PropTypes.bool.isRequired,
  isPortalEmployer: PropTypes.bool,
  audienceMemberIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  audienceMembers: audienceMembersType.isRequired,
  burst: PropTypes.bool,
  numberOfPositions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isPortalUser: PropTypes.bool.isRequired,
  recruiterCompanyName: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
};

JobProfileView.defaultProps = {
  audienceId: null,
  agencyBriefing: null,
  contractDuration: null,
  contractDurationInterval: null,
  interviewComments: null,
  isRecruiter: null,
  jobStatus: null,
  keySkills: null,
  engagementId: null,
  postcode: null,
  salaryMax: null,
  salaryRange: null,
  step: "",
  vacancyReason: null,
  jobStatusWidget: null,
  finalSalary: null,
  burst: null,
  numberOfPositions: null,
  dateCreated: null,
  isPortalEmployer: null,
};

export default JobProfileView;
